import React, { useEffect, useState } from 'react'
import { getAuthToken, refreshToken } from 'lib/auth'
import { useLocation } from 'react-router-dom'
import { bindActionCreators, Dispatch } from 'redux'
import { Route } from 'react-router'
import { connect } from 'react-redux'
import history from 'utils/history'
import { ROLES } from 'config'
import { IdleTrackingContainer } from 'pages/Common/container'
import {
  getUserProfileAction,
  setTenantIdAction,
  updateUserProfileAction,
  getUnreadNotificationsAction,
} from 'store/actions/user'
import isEmpty from 'lodash/isEmpty'
import { logoutAction } from 'store/actions/auth'
import { getCurrentRoute } from 'utils/helper'
import redirectTo from 'utils/redirectTo'
import moment from 'moment-timezone'
import { getBSOProfileAction } from 'store/actions/bso'
import { RootState } from 'App'
import Loader from 'components/Loaders'
import { AppFloatingChat } from 'components/Common/AppFloatingChat/AppFloatingChat.component'
import { useFeatureFlag } from 'hooks/userFeatureFlag'

/**
 * PrivateRoute is used to support the react router and it renders the routes
 * which is marked as private or is only accessible authenticated users
 * @param {React.Component} component
 */

const setTimezone = (timezone: string) => {
  moment.tz.setDefault(timezone ?? moment.tz.guess())
}

const PrivateRoute = ({
  user,
  component,
  userTenantId,
  logoutAction,
  getUserProfileAction,
  isUpdateProfileLoading,
  updateUserProfileAction,
  setTenantIdAction,
  getUnreadNotificationsAction,
  getBSOProfileAction,
  isBsoProfileExist,
  isV2,
  ...rest
}) => {
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const isFloatingChatEnabled = useFeatureFlag(user?.id, 'release-floating-chat')

  useEffect(() => {
    getUnreadNotificationsAction()
    // eslint-disable-next-line
  }, [window.location.href])

  useEffect(() => {
    ;(async () => {
      const jwtToken = await getAuthToken()
      if (!jwtToken) {
        const updatedSession = await refreshToken()
        if (!updatedSession.isValid()) {
          redirectTo('/auth/login', getCurrentRoute())
          return
        }
      }
    })()
  }, [])

  // Fetch user profile on mount to ensure we have the latest permissions
  // This is especially important when admin rights are granted/revoked
  useEffect(() => {
    getUserProfileAction()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isEmpty(user?.id)) {
      // Wait for user profile to be fetched
      setLoading(true)
    } else {
      let found = false
      const roles = user.roles
      const pathName = location.pathname
      // Extract tenantId from URL path (format: /:role/:tenantId/:...)
      // tenantId is a string from URL segment, not an array
      let tenantId = pathName?.split('/')[2]
      const words = pathName.split('/')
      const pathRole = words[1]

      // Map user roles to allowed path prefixes (including v2 variants)
      const rolePathMappings: Record<string, string[]> = {
        BSO_ADMIN: [ROLES.BSO_ADMIN, ROLES.V2_ADMIN],
        BSO_ADVISOR: [ROLES.BSO_ADVISOR, ROLES.V2_ADVISOR],
        BUSINESS_OWNER: [ROLES.BUSINESS_OWNER, ROLES.V2],
      }

      roles.forEach((role) => {
        const allowedPaths = rolePathMappings[role] || []
        if (allowedPaths.includes(pathRole)) {
          found = true
        }
      })
      if (isEmpty(userTenantId)) {
        // tenantId from URL is a string; fallback to user's tenantId if URL parsing failed
        const finalTenantId = tenantId || user?.tenantId
        setTenantIdAction(finalTenantId)
        tenantId = finalTenantId
      }

      // get bso profile
      if (!isBsoProfileExist) {
        getBSOProfileAction(tenantId)
      }

      if (!user?.timezone) {
        updateUserProfileAction({
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
      }

      if (!found) {
        history.goBack()
      }
      setLoading(false)
      setTimezone(user?.timezone)
    }
    // location.pathname is included to re-run permission check when route changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname])

  const routeComponent = (props) => React.createElement(component, props)

  return (
    <>
      {!loading ? (
        <>
          <IdleTrackingContainer />
          <Route {...rest} render={routeComponent} />
          {isFloatingChatEnabled && <AppFloatingChat sessionId={userTenantId} />}
        </>
      ) : (
        <Loader loader="LogoLoader" />
      )}
    </>
  )
}

const mapProps = (state: RootState) => {
  const user = state.user
  const isBsoProfileExist = Boolean(state.bso.bsoProfile.id ?? '')
  return {
    user: user?.user,
    userTenantId: user?.tenantId,
    isUpdateProfileLoading: state?.user?.isUpdateProfileLoading,
    isBsoProfileExist,
  }
}

function mapDispatch(dispatch: Dispatch) {
  return bindActionCreators(
    {
      getUserProfileAction: getUserProfileAction.STARTED,
      getUnreadNotificationsAction: getUnreadNotificationsAction.STARTED,
      setTenantIdAction: setTenantIdAction,
      logoutAction: logoutAction,
      updateUserProfileAction: updateUserProfileAction.STARTED,
      getBSOProfileAction: getBSOProfileAction.STARTED,
    },
    dispatch
  )
}

export default connect(mapProps, mapDispatch)(PrivateRoute)
